/**
 * RENDA-K の連打プラン生成ロジック。
 *
 * 間隔カーブ（開始/終了音価 + 曲線 + アッチェレランド）を log2 ドメインで
 * 補間し、連打ヒット列（時刻・間隔・再生レート）を返す純関数群。
 * 試聴（useStutterEngine）・WAV 書き出し（renderStutter）・間隔エディタの
 * 描画（IntervalEditor）が同じ結果を共有する。
 *
 * 音価は「ステップ」で表す: step 0 = 64分音符（1/16 拍）、1 上がるごとに
 * 2 倍で step 5 = 2分音符（2 拍）。intervalBeats = 2^step / 16。
 */

/** 縦軸の下限（64分音符） */
export const STEP_MIN = 0;
/** 縦軸の上限（2分音符） */
export const STEP_MAX = 5;
/**
 * アッチェレランドで押し下げられる実効ステップの下限（1/256 音符相当）。
 * 軸の下限 1/64 よりさらに 2 オクターブ速くまで許す。
 */
export const EFFECTIVE_STEP_MIN = -2;

/** 間隔エディタの縦軸に並ぶ音価（step が整数のときのラベル） */
export const INTERVAL_STEPS = [
  { step: 0, label: "1/64" },
  { step: 1, label: "1/32" },
  { step: 2, label: "1/16" },
  { step: 3, label: "1/8" },
  { step: 4, label: "1/4" },
  { step: 5, label: "1/2" },
] as const;

/** SPEED ノブ最大時のスライス再生レート */
export const SPEED_RATE_MAX = 4;
/** ACCEL 100% がフレーズ終端で間隔を圧縮するステップ量（2 = 2 オクターブ = 1/4 倍） */
export const ACCEL_STEP_OFFSET_MAX = 2;

/** 暴走防止の上限（実用上は 1/256 間隔 × 1 小節でも数百発に収まる） */
export const MAX_HITS = 2000;
const MIN_INTERVAL_SEC = 0.001;
/** 終端で切り詰めた結果これ未満になるヒットは落とす（クリックノイズ防止） */
const MIN_LAST_SLICE_SEC = 0.003;

export interface RendaParams {
  bpm: number;
  /** 連打開始位置（元音源内の秒） */
  startSec: number;
  /** 連打の長さ（16分音符単位、1..16 = 最大1小節） */
  lengthSixteenths: number;
  /** 間隔カーブの開始ステップ（0..5、エディタで音価にスナップ） */
  intervalStartStep: number;
  /** 間隔カーブの終了ステップ（0..5） */
  intervalEndStep: number;
  /** 曲線 -100..100（0 = 直線、+で終端寄りに変化が集中） */
  curveAmount: number;
  /** true で各ヒットの間隔を音価（整数ステップ）にスナップ */
  quantize: boolean;
  /** スライス再生速度 0..100（0 = 等倍 = 音符の長さ、100 = ×4） */
  speed: number;
  /** アッチェレランド 0..100（時間経過で間隔を圧縮、終端で最大 2 オクターブ） */
  accel: number;
}

export const DEFAULT_RENDA_PARAMS: RendaParams = {
  bpm: 140,
  startSec: 0,
  lengthSixteenths: 16,
  intervalStartStep: 2,
  intervalEndStep: 2,
  curveAmount: 0,
  quantize: false,
  speed: 0,
  accel: 0,
};

export interface StutterHit {
  /** フレーズ先頭からのオフセット（秒） */
  timeSec: number;
  /** 次のヒットまで = このスライスの可聴長（秒）。末尾はフレーズ終端で切り詰め */
  intervalSec: number;
  /** このヒットの実効ステップ（量子化・アクセル込み。エディタ描画用） */
  step: number;
  /** スライスの再生レート（SPEED ノブ由来） */
  playbackRate: number;
}

export const stepToBeats = (step: number): number => 2 ** step / 16;

export const beatsToSeconds = (beats: number, bpm: number): number =>
  beats * (60 / bpm);

export const phraseLengthBeats = (lengthSixteenths: number): number =>
  lengthSixteenths * 0.25;

export const phraseLengthSec = (
  lengthSixteenths: number,
  bpm: number,
): number => beatsToSeconds(phraseLengthBeats(lengthSixteenths), bpm);

export const speedToPlaybackRate = (speed: number): number =>
  1 + (SPEED_RATE_MAX - 1) * (speed / 100);

/**
 * 曲線シェイプ。p^(2^(c/50)) で、c=0 が直線、c=+100 は前半に開始値を
 * 保って終端で一気に変化、c=-100 はその逆（冒頭で一気に変化）。
 */
const curveShape = (p: number, curveAmount: number): number =>
  Math.pow(p, 2 ** (curveAmount / 50));

/**
 * 位置 p（0..1）における間隔カーブの生ステップ値。
 * アクセル・量子化は含まない（エディタのカーブ線描画にも使う）。
 */
export const stepCurveAt = (params: RendaParams, p: number): number =>
  params.intervalStartStep +
  (params.intervalEndStep - params.intervalStartStep) *
    curveShape(p, params.curveAmount);

/**
 * 位置 p（0..1）における実効ステップ。
 * アッチェレランドのステップオフセットを足し、量子化 ON なら音価に
 * スナップした上で [EFFECTIVE_STEP_MIN, STEP_MAX] にクランプする。
 */
export const effectiveStepAt = (params: RendaParams, p: number): number => {
  const accelOffset = -ACCEL_STEP_OFFSET_MAX * (params.accel / 100) * p;
  let step = stepCurveAt(params, p) + accelOffset;
  if (params.quantize) step = Math.round(step);
  return Math.min(STEP_MAX, Math.max(EFFECTIVE_STEP_MIN, step));
};

/**
 * 連打ヒット列を生成する。フレーズ先頭 t=0 から間隔カーブに従って
 * ヒットを置き、最後のヒットはフレーズ終端ぴったりに切り詰める
 * （差し替え後の元音源と隙間なく接続するため）。
 */
export const buildStutterPlan = (params: RendaParams): StutterHit[] => {
  const lengthSec = phraseLengthSec(params.lengthSixteenths, params.bpm);
  const playbackRate = speedToPlaybackRate(params.speed);
  const hits: StutterHit[] = [];

  let t = 0;
  while (t < lengthSec - 1e-9 && hits.length < MAX_HITS) {
    const p = t / lengthSec;
    const step = effectiveStepAt(params, p);
    const intervalSec = Math.max(
      beatsToSeconds(stepToBeats(step), params.bpm),
      MIN_INTERVAL_SEC,
    );
    hits.push({ timeSec: t, intervalSec, step, playbackRate });
    t += intervalSec;
  }

  if (hits.length === 0) return hits;

  // ループ終了時点で最後のヒットの自然な終端はフレーズ終端以降にあるため、
  // 切り詰めは常に非負。極小になる場合はヒット自体を落として前のヒットを延ばす。
  const last = hits[hits.length - 1];
  const clampedSec = lengthSec - last.timeSec;
  if (clampedSec < MIN_LAST_SLICE_SEC && hits.length >= 2) {
    hits.pop();
    const prev = hits[hits.length - 1];
    prev.intervalSec = lengthSec - prev.timeSec;
  } else {
    last.intervalSec = clampedSec;
  }

  return hits;
};
